import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  BookOpen,
  Play,
  Search,
  Zap,
  Smile,
  PenTool,
  Mic,
  Heart,
  Sun,
  Feather,
  Star,
  FolderOpen,
  ArrowUpDown,
  SmileIcon,
  Bell,
  Crown,
  Clock,
  Dumbbell,
  Baby,
  Book,
  Target,
  Music,
} from "lucide-react";
import { Link } from "react-router-dom";
import grumpycloud from "../Photos/GrumpyCloud.png";
import Safari from "../Photos/Safari.png";
import SilentTree from "../Photos/SilentTree.png";
import Musical from "../Photos/Musical.png";
import Croco from "../Photos/Crocodile.png";
import Zippy from "../Photos/Zippy.png";
import TheR from "../Photos/TheR.png";
import Frog from "../Photos/Frog.png";
import StoryPose from "../Photos/StoryPose1.png";
import ABC from "../Photos/ABC DANCE.png";

// --- Framer Motion Variants ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: "easeOut" } },
};

// --- Story Data ---
const ALL_STORIES = [
  {
    id: 101,
    title: "The Grumpy Cloud's Sunny Day 🌤️",
    desc: "A cloud learns to express frustration and find happiness. Focuses on vocal emotional tone.",
    category: "Emotional Regulation",
    icon: Smile,
    duration: "1 min",
    ageRange: "3-5",
    date: new Date("2024-12-19"),
    popularity: 150,
    imageUrl: grumpycloud,
    releaseStatus: "released",
  },
  {
    id: 102,
    title: "The Great 'S' Sound Safari 🦓",
    desc: "Join Sassy the Snake, emphasizing clear 'S' sounds and slow, steady speech patterns.",
    category: "Phonics Focus",
    icon: Mic,
    duration: "1 min",
    ageRange: "6-8",
    date: new Date("2024-11-15"),
    popularity: 220,
    imageUrl: Safari,
    releaseStatus: "released",
  },
  {
    id: 103,
    title: "The Talking Tree's Secret Whispers 🌳",
    desc: "An interactive story where children must speak quietly and clearly to hear the tree's secret clues.",
    category: "Volume Control",
    icon: Heart,
    duration: "1 min",
    ageRange: "3-5",
    date: new Date("2024-09-20"),
    popularity: 180,
    imageUrl: SilentTree,
    releaseStatus: "released",
  },
  {
    id: 104,
    title: "Zippy the Zoom Racer's Fast Talk 🏎️",
    desc: "Challenge kids to match the speed and tone of the race announcer to win the race. High energy!",
    category: "High Energy",
    icon: Zap,
    duration: "1 min",
    ageRange: "6-8",
    date: new Date("2024-11-05"),
    popularity: 300,
    imageUrl: Zippy,
    releaseStatus: "released",
  },
  {
    id: 105,
    title: "Freddie the Frog's Feelings 🐸",
    desc: "Freddie feels all sorts of ways! A story to practice naming and identifying different emotions vocally.",
    category: "Emotional Regulation",
    icon: Feather,
    duration: "1 min",
    ageRange: "3-5",
    date: new Date("2024-08-25"),
    popularity: 110,
    imageUrl: Frog,
    releaseStatus: "released",
  },
  {
    id: 106,
    title: "The Magical Mystery of 'R' 🌈",
    desc: "Rhinos, rainbows, and red robots! A colorful adventure focusing on the tricky 'R' sound.",
    category: "Phonics Focus",
    icon: Star,
    duration: "1 min",
    ageRange: "9-12",
    date: new Date("2024-11-10"),
    popularity: 250,
    imageUrl: TheR,
    releaseStatus: "released",
  },
  {
    id: 107,
    title: "Counting Crocodiles' Dinner 🐊",
    desc: "A fun counting challenge where kids must clearly count the crocodiles before they eat dinner. Early math focus.",
    category: "Early Math & Logic",
    icon: Sun,
    duration: "1 min",
    ageRange: "3-5",
    date: new Date("2024-10-15"),
    popularity: 90,
    imageUrl: Croco,
    releaseStatus: "released",
  },
  {
    id: 108,
    title: "Melody the Musical Martian 👽",
    desc: "An alien who only understands singing! Kids sing back simple melodies to help Melody navigate space.",
    category: "Rhythm & Music",
    icon: BookOpen,
    duration: "1 min",
    ageRange: "6-8",
    date: new Date("2024-11-18"),
    popularity: 400,
    imageUrl: Musical,
    releaseStatus: "released",
  },
 {
    id: 109,
    title: "ABC Dance Song 💃",
    desc: "Get moving while learning! Kids dance along to the alphabet, using their bodies to form letter shapes as they go.",
    category: "Rhythm & Music",
    icon: Music, // or BookOpen depending on your imports
    duration: "1 min",
    ageRange: "3-5",
    date: new Date("2025-12-16"),
    popularity: 450,
    imageUrl: ABC, // Replace with a specific 'Dance' image if available
    releaseStatus: "released",
  },
];

// --- Categories ---
const STORY_CATEGORIES = [
  { name: "All Stories", icon: BookOpen },
  { name: "Emotional Regulation", icon: Smile },
  { name: "Phonics Focus", icon: Mic },
  { name: "Volume Control", icon: Heart },
  { name: "Rhythm & Music", icon: Star },
  { name: "Early Math & Logic", icon: Sun },
  { name: "High Energy", icon: Zap },
];

// --- Story Card ---
const StoryCard = ({ story }) => {
  const {
    id,
    title,
    desc,
    category,
    duration,
    ageRange,
    popularity,
    date,
    imageUrl,
    releaseStatus,
    isPremium,
    level,
    targetSkill,
    speakerVoice,
  } = story;

  const storyLinkPath = `/story/${id}`;
  const isReleased = releaseStatus === "released";

  const formattedDate = new Date(date).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
  });

  const renderActionButton = () => {
    const base =
      "w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-bold text-white shadow transition-all text-base";

    if (isReleased) {
      return (
        <Link to={storyLinkPath} className="w-full block">
          <motion.div
            className={`${base} bg-indigo-600 hover:bg-indigo-700`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Play className="w-5 h-5" /> Listen
          </motion.div>
        </Link>
      );
    }

    return (
      <div className="w-full block cursor-not-allowed">
        <div className={`${base} bg-gray-400`}>
          <Play className="w-5 h-5" /> Coming Soon
        </div>
      </div>
    );
  };

  return (
    <motion.article
      layout
      className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-b-4 border-sky-500 hover:border-sky-700 overflow-hidden flex flex-col relative"
    >
      {isPremium && (
        <div className="absolute top-0 right-0 z-10 bg-amber-500 text-white text-sm font-bold px-3 py-1 rounded-bl-xl shadow-md">
          <Crown className="w-4 h-4 inline mr-1" /> Premium
        </div>
      )}

      <div className="h-44 sm:h-52 relative overflow-hidden">
        <img src={imageUrl} alt={title} className="w-full h-full object-cover" />
      </div>

      <div className="p-4 flex flex-col flex-1">
        <div className="flex-1 flex flex-col gap-3">
          <div className="flex justify-between items-start">
            <h2 className="text-xl font-extrabold text-indigo-800 leading-snug pr-2">{title}</h2>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <span className="text-sm font-semibold text-white bg-violet-600 px-3 py-1 rounded-full shadow">
                <Clock className="w-4 h-4 inline mr-1" /> {duration}
              </span>
            </div>
          </div>

          <p className="text-gray-700 text-sm leading-snug line-clamp-3">{desc}</p>

          <div className="grid grid-cols-2 gap-y-2 text-sm text-gray-800 mt-2 border-b border-gray-200 pb-2">
            <p className="flex items-center gap-2">
              <Baby className="w-4 h-4 text-sky-600" /> Age: {ageRange}
            </p>
            <p className="flex items-center gap-2">
              <Star className="w-4 h-4 text-sky-600" /> Pop: {popularity}
            </p>
            <p className="flex items-center gap-2">
              <Mic className="w-4 h-4 text-sky-600" /> Voice: {speakerVoice}
            </p>
            <p className="flex items-center gap-2">
              <Book className="w-4 h-4 text-sky-600" /> {category}
            </p>
          </div>

          <p className="text-sm font-semibold text-sky-800 bg-sky-100 px-3 py-1 rounded-full inline-flex items-center gap-2 w-fit self-center mt-1">
            <Target className="w-4 h-4" /> {targetSkill || category}
          </p>

          <p className="text-center text-sm mt-1">
            <span className={`font-bold ${isReleased ? "text-green-600" : "text-orange-600"}`}>
              {isReleased ? "Available Now" : "Coming Soon"}
            </span>
            {/* <span className="text-gray-500"> • {formattedDate}</span> */}
          </p>
        </div>

        <div className="pt-3 border-t border-gray-100">{renderActionButton()}</div>
      </div>
    </motion.article>
  );
};

// --- Main Page ---
export default function TalkyToonStoryPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("All Stories");
  const [sortOption, setSortOption] = useState("newest");
  const [ageFilter, setAgeFilter] = useState("");
  const [releaseFilter, setReleaseFilter] = useState("all"); // all | released | upcoming

  const resetFilters = () => {
    setActiveCategory("All Stories");
    setSortOption("newest");
    setAgeFilter("");
    setSearchTerm("");
    setReleaseFilter("all");
  };

  // --- Filtering & Sorting ---
  const filteredStories = useMemo(() => {
    let tempStories = ALL_STORIES.filter((story) => {
      const categoryMatch = activeCategory === "All Stories" || story.category === activeCategory;
      const searchMatch =
        story.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        story.desc.toLowerCase().includes(searchTerm.toLowerCase()) ||
        story.category.toLowerCase().includes(searchTerm.toLowerCase());
      const ageMatch = ageFilter === "" || story.ageRange === ageFilter;
      const releaseMatch =
        releaseFilter === "all" || story.releaseStatus === releaseFilter;

      return categoryMatch && searchMatch && ageMatch && releaseMatch;
    });

    switch (sortOption) {
      case "az":
        tempStories.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case "za":
        tempStories.sort((a, b) => b.title.localeCompare(a.title));
        break;
      case "newest":
        tempStories.sort((a, b) => b.date.getTime() - a.date.getTime());
        break;
      case "oldest":
        tempStories.sort((a, b) => a.date.getTime() - b.date.getTime());
        break;
      case "popular":
        tempStories.sort((a, b) => b.popularity - a.popularity);
        break;
      default:
        tempStories.sort((a, b) => b.date.getTime() - a.date.getTime());
        break;
    }

    return tempStories;
  }, [searchTerm, activeCategory, sortOption, ageFilter, releaseFilter]);

  // Separate released & upcoming
  const releasedStories = filteredStories.filter((s) => s.releaseStatus === "released");
  const upcomingStories = filteredStories.filter((s) => s.releaseStatus !== "released");

  return (
    <div className="min-h-screen bg-slate-50 text-gray-800 font-sans overflow-x-hidden relative">
      <div
        className="fixed inset-0 opacity-20 pointer-events-none"
        aria-hidden="true"
        style={{
          backgroundImage:
            "radial-gradient(circle at 10% 90%, rgba(203,213,225,0.3) 0%, transparent 60%), radial-gradient(circle at 90% 10%, rgba(129,140,248,0.3) 0%, transparent 60%)",
        }}
      />

      <main className="relative z-10 lg:grid lg:grid-cols-[280px_1fr] lg:gap-12 lg:pr-6">
        {/* LEFT SIDEBAR (search, category, age, sort, release filter) */}
        <section className="px-4 sm:px-6 mb-12 sm:mb-16 lg:order-1">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1, duration: 0.7 }}
            className="lg:sticky lg:top-8 space-y-8"
          >
            <br />
            {/* Search */}
            <div className="relative shadow-md rounded-full bg-white border border-gray-200 w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-cyan-500" />
              <input
                type="text"
                placeholder="Search stories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-6 py-3 text-base rounded-full focus:outline-none focus:ring-2 focus:ring-cyan-300 transition-shadow bg-transparent"
              />
            </div>

            {/* Category */}
            <div className="flex flex-col gap-3 pb-2 w-full">
              <h3 className="text-lg font-semibold text-gray-800 mb-1 flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-cyan-600" /> Categories
              </h3>
              {STORY_CATEGORIES.map((category) => {
                const isActive = activeCategory === category.name;
                const CategoryIcon = category.icon;
                return (
                  <motion.button
                    key={category.name}
                    onClick={() => setActiveCategory(category.name)}
                    className={`flex items-center justify-start gap-3 px-4 py-3 rounded-lg font-medium text-sm transition-all shadow-sm w-full ${
                      isActive
                        ? "bg-cyan-600 text-white shadow-cyan-300"
                        : "bg-white text-gray-700 border border-gray-200 hover:border-cyan-400 hover:shadow-md"
                    }`}
                    whileHover={{ x: 5 }}
                    whileTap={{ scale: 0.98 }}
                    aria-pressed={isActive}
                  >
                    <CategoryIcon className="w-5 h-5" /> {category.name}
                  </motion.button>
                );
              })}
            </div>

            {/* Release Filter */}
            <div className="space-y-3 pt-4 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Bell className="w-5 h-5 text-cyan-600" /> Release Status
              </h3>
              <select
                value={releaseFilter}
                onChange={(e) => setReleaseFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-700 focus:ring-2 focus:ring-cyan-300 focus:outline-none bg-white"
              >
                <option value="all">All Stories</option>
                <option value="released">Released</option>
                <option value="upcoming">Upcoming</option>
              </select>
            </div>

            {/* Sort */}
            <div className="space-y-3 pt-4 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <ArrowUpDown className="w-5 h-5 text-cyan-600" /> Sort Stories
              </h3>
              <select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-700 focus:ring-2 focus:ring-cyan-300 focus:outline-none bg-white"
              >
                <option value="newest">Newest First</option>
                <option value="popular">Most Popular</option>
                <option value="az">Title: A → Z</option>
                <option value="za">Title: Z → A</option>
                <option value="oldest">Oldest First</option>
              </select>
            </div>

            {/* Age Filter */}
            <div className="space-y-3 pt-4 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <SmileIcon className="w-5 h-5 text-cyan-600" /> Age Group
              </h3>
              <select
                value={ageFilter}
                onChange={(e) => setAgeFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-700 focus:ring-2 focus:ring-cyan-300 focus:outline-none bg-white"
              >
                <option value="">All Ages</option>
                <option value="3-5">Ages 3–5</option>
                <option value="6-8">Ages 6–8</option>
                <option value="9-12">Ages 9–12</option>
              </select>
            </div>

            {/* Reset Filters */}
            <button
              onClick={resetFilters}
              className="w-full bg-gray-100 text-gray-700 px-4 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-200 hover:border-gray-400 transition-all"
            >
              Reset Filters
            </button>
          </motion.div>
        </section>

        {/* RIGHT CONTENT */}
        <section className="lg:order-2 px-4 sm:px-6 lg:px-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
          >
            <br />
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-700 mb-8 sm:mb-10 lg:text-left">
              {activeCategory === "All Stories"
                ? "All Adventures"
                : `Stories about: ${activeCategory}`}{" "}
              ({filteredStories.length})
            </h2>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {filteredStories.length > 0 ? (
              filteredStories.map((story) => (
                <StoryCard key={story.id} story={story} />
              ))
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="md:col-span-2 xl:col-span-3 text-center py-12 sm:py-20 bg-white/80 rounded-xl shadow-inner border border-dashed border-cyan-300"
              >
                <Sun className="w-10 h-10 sm:w-12 sm:h-12 text-cyan-400 mx-auto mb-4" />
                <p className="text-lg sm:text-xl text-gray-700 font-semibold">
                  Shhh... that story is a secret!
                </p>
                <p className="text-gray-500 text-sm sm:text-base">
                  Try a different filter or search term to find a new adventure.
                </p>
              </motion.div>
            )}
          </motion.div>
        </section>
      </main>

{/* REVISED NEXT ADVENTURE SECTION */}
{/* <section className="w-full bg-blue-50 py-16 sm:py-24 relative overflow-hidden">
  <div
    className="absolute inset-0 opacity-40 pointer-events-none"
    aria-hidden="true"
    style={{ 
        backgroundImage: 'radial-gradient(at 10% 50%, #fef3c7, transparent 70%), radial-gradient(at 90% 10%, #bfdbfe, transparent 70%)',
    }}
  />
  
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.9, type: "spring", stiffness: 80 }}
    viewport={{ once: true, amount: 0.4 }}
    className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
  >
    <div className="bg-white rounded-[3rem] shadow-2xl shadow-pink-300 p-8 sm:p-14 border-8 border-pink-400">
      <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
        
        <motion.div 
          className="lg:w-1/2 w-full flex items-center justify-center order-2 lg:order-1"
          whileHover={{ scale: 1.08, rotate: 2 }} 
          transition={{ type: "spring", stiffness: 150 }}
        >
          <img
            src={StoryPose}
            alt="TalkyToon toucan character bouncing excitedly"
            className="w-full max-w-sm lg:max-w-md object-contain drop-shadow-2xl shadow-lg shadow-yellow-300"
          />
        </motion.div>
        
        <div className="lg:w-1/2 w-full flex flex-col justify-center items-center text-center order-1 lg:order-2">
          
          <h3 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-blue-700 mb-2">
            New Fun is Loading!
          </h3>
          <br></br>
          
          <p className="text-2xl sm:text-3xl font-black text-pink-600 mb-6 sm:mb-8 bg-yellow-100 px-4 py-2 rounded-xl border-2 border-yellow-300 shadow-md">
            Find us: @TalkyToonOfficial
          </p>
          
          <p className="text-gray-700 text-lg sm:text-xl max-w-md leading-relaxed mb-8">
            Get ready for awesome new stories and songs ! Don't miss the next adventure—tap the big red button below!
          </p>

          <motion.a
            href="https://www.youtube.com/@TalkyToonOfficial"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.1, boxShadow: "0 12px 25px rgba(220, 38, 38, 0.7)", y: -2 }} 
            whileTap={{ scale: 0.95 }}
            className="flex items-center justify-center gap-3 bg-red-600 hover:bg-red-700 text-white font-extrabold px-12 py-5 rounded-full shadow-2xl transition-all text-xl uppercase tracking-wider"
          >
            <svg
              className="w-7 h-7"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M19.615 3.184a2.998 2.998 0 0 0-2.12-2.12C15.167.75 12 0.75 12 0.75s-3.167 0-5.495.314a2.998 2.998 0 0 0-2.12 2.12A31.978 31.978 0 0 0 3 12a31.978 31.978 0 0 0 1.285 8.816 2.998 2.998 0 0 0 2.12 2.12c2.328.314 5.495.314 5.495.314s3.167 0 5.495-.314a2.998 2.998 0 0 0 2.12-2.12A31.978 31.978 0 0 0 21 12a31.978 31.978 0 0 0-1.385-8.816zM10 15.5v-7l6 3.5-6 3.5z" />
            </svg>
            Subscribe & Play!
          </motion.a>
        </div>
        
      </div>
    </div>
  </motion.div>
</section> */}
<br></br>

    </div>
  );
}
